import { useLocation, useNavigate } from 'react-router-dom';
import { useEnv, type EnvName } from '../config/env';
import { VENUE_PARAM } from '../venue/scope';
import { GHOST_PARAM } from '../wallet/GhostProvider';
import { toasts } from './toastStore';
import { t as tr, type PlainKey } from '../i18n/i18n';
import { PANEL_PARAM } from '../catalog/layout';
import { useT } from '../i18n/useT';

const OPTIONS: { name: EnvName; label: PlainKey }[] = [
  { name: 'testnet', label: 'shell.env.testnet' },
  { name: 'mainnet', label: 'shell.env.mainnet' },
];

/** Query params that describe the viewer, not the page: they survive an env switch. */
const KEEP_PARAMS = [GHOST_PARAM, VENUE_PARAM];

/** Search string with only the viewer-scoped params of `search` ("" or "?ghost=…&venue=…"). */
function keptSearch(search: string): string {
  const prev = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const k of KEEP_PARAMS) {
    const v = prev.get(k);
    if (v !== null) next.set(k, v);
  }
  const s = next.toString();
  return s ? `?${s}` : '';
}

export default function EnvToggle() {
  const { env, setEnv } = useEnv();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();

  const select = (name: EnvName) => {
    if (name === env.name) return;
    // Outcome ids are per network, so a market page would show "not found" after the switch (#707).
    // Go to Markets instead, keeping ?ghost= / ?venue=, and say why.
    // The inline panel on Markets (#1953) holds an outcome id too.
    if (location.pathname.startsWith('/market/') || new URLSearchParams(location.search).has(PANEL_PARAM)) {
      navigate({ pathname: '/', search: keptSearch(location.search) });
      toasts.push({
        tone: 'info',
        title: tr('shell.env.switched', { network: tr(OPTIONS.find((o) => o.name === name)?.label ?? 'shell.env.testnet') }),
        body: tr('shell.env.switched_body'),
      });
    }
    setEnv(name);
  };

  return (
    <div role="radiogroup" aria-label={t('shell.env.label')} data-tour="env" className="flex rounded-lg bg-surface-2 p-0.5 text-xs">
      {OPTIONS.map((o) => {
        const active = env.name === o.name;
        const activeCls = o.name === 'mainnet' ? 'bg-warn text-warn-fg' : 'bg-accent/20 text-accent';
        return (
          <button
            key={o.name}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => select(o.name)}
            className={`rounded-md px-2 py-1 font-medium transition-colors sm:px-2.5 ${
              active ? activeCls : 'text-muted hover:text-fg'
            }`}
          >
            {t(o.label)}
          </button>
        );
      })}
    </div>
  );
}
